import { Expression } from '~/prix/types/expression'

export interface AgeRange {
  key:
    | 'newborn'
    | 'infant'
    | 'toddler'
    | 'preschooler'
    | 'child'
    | 'adolescent'
    | 'youngAdult'
    | 'earlyAdulthood'
    | 'midlifeAdulthood'
    | 'youngElderly'
    | 'elderly'
    | 'oldAge'
  singular: string
  plural: string
  description: string
  minimum: number
  maximum: number
}

const ageRanges: AgeRange[] = [
  {
    key: 'newborn',
    singular: 'Recém-nascido',
    plural: 'Recém-nascidos',
    description: 'Do nascimento até 28 dias de vida',
    minimum: 0,
    maximum: 0.08,
  },
  {
    key: 'infant',
    singular: 'Bebê',
    plural: 'Bebês',
    description: 'De 1 mês a 12 meses de idade',
    minimum: 0.08,
    maximum: 1,
  },
  {
    key: 'toddler',
    singular: 'Criança pequena',
    plural: 'Crianças pequenas',
    description: 'De 1 a 3 anos',
    minimum: 1,
    maximum: 3,
  },
  {
    key: 'preschooler',
    singular: 'Criança pré-escolar',
    plural: 'Crianças pré-escolares',
    description: 'De 3 a 5 anos',
    minimum: 3,
    maximum: 6,
  },
  {
    key: 'child',
    singular: 'Criança',
    plural: 'Crianças',
    description: 'De 6 a 12 anos',
    minimum: 6,
    maximum: 13,
  },
  {
    key: 'adolescent',
    singular: 'Adolescente',
    plural: 'Adolescentes',
    description: 'De 13 a 17 anos',
    minimum: 13,
    maximum: 18,
  },
  {
    key: 'youngAdult',
    singular: 'Jovem adulto',
    plural: 'Jovens adultos',
    description: 'De 18 a 24 anos',
    minimum: 18,
    maximum: 25,
  },
  {
    key: 'earlyAdulthood',
    singular: 'Adulto jovem',
    plural: 'Adultos jovens',
    description: 'De 25 a 44 anos',
    minimum: 25,
    maximum: 45,
  },
  {
    key: 'midlifeAdulthood',
    singular: 'Adulto de meia-idade',
    plural: 'Adultos de meia-idade',
    description: 'De 45 a 64 anos',
    minimum: 45,
    maximum: 65,
  },
  {
    key: 'youngElderly',
    singular: 'Idoso jovem',
    plural: 'Idosos jovens',
    description: 'De 65 a 74 anos',
    minimum: 65,
    maximum: 75,
  },
  {
    key: 'elderly',
    singular: 'Idoso',
    plural: 'Idosos',
    description: 'De 75 a 84 anos',
    minimum: 75,
    maximum: 85,
  },
  {
    key: 'oldAge',
    singular: 'Idoso de idade avançada',
    plural: 'Idosos de idade avançada',
    description: '85 anos em diante',
    minimum: 85,
    maximum: 300,
  },
]

export default ageRanges

export const ageRangesEnumeratedOptions = ageRanges.reduce((acc, ageRange) => {
  acc[ageRange.key] = `${ageRange.plural} (${ageRange.description.toLowerCase()})`
  return acc
}, {} as Record<AgeRange['key'], string>)

export function classifyAgeRange(age: number): AgeRange {
  if (age < 0) {
    throw new Error('Age cannot be negative.')
  }
  const ageRange = ageRanges.find(ageRange => {
    return age >= ageRange.minimum && (ageRange.maximum === null || age < ageRange.maximum)
  })

  if (!ageRange) {
    throw new Error('No age range found for the provided age.')
  }

  return ageRange
}

export function ageRangeExpression(ageRange: AgeRange, decimalAgeProperty: string): Expression {
  return {
    type: 'expression',
    kind: 'binary',
    left: {
      type: 'expression',
      kind: 'binary',
      operator: '>=',
      left: {
        type: 'expression',
        kind: 'property',
        property: decimalAgeProperty,
      },
      right: {
        type: 'expression',
        kind: 'literal',
        literalType: 'number',
        value: ageRange.minimum,
      },
    },
    operator: 'AND',
    right: {
      type: 'expression',
      kind: 'binary',
      operator: '<',
      left: {
        type: 'expression',
        kind: 'property',
        property: decimalAgeProperty,
      },
      right: {
        type: 'expression',
        kind: 'literal',
        literalType: 'number',
        value: ageRange.maximum,
      },
    },
  }
}

export function getAgeRangesExpressions(
  decimalAgeProperty: string,
): Record<AgeRange['key'], Expression> {
  return ageRanges.reduce((acc, ageRange) => {
    acc[ageRange.key] = ageRangeExpression(ageRange, decimalAgeProperty)
    return acc
  }, {} as Record<AgeRange['key'], Expression>)
}
